import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Click-to-sort for tables.
 *
 * useSort(rows, accessors, initial?) returns the sorted rows plus the state
 * and toggle to wire into SortHeader cells. First click on a column sorts
 * descending (the "show me the biggest problem" direction for this product),
 * second click flips ascending. Nulls always sort last, in either direction,
 * so empty cells never crowd the top of the list.
 */

export type SortDir = "asc" | "desc";
export interface SortState {
  key: string;
  dir: SortDir;
}

// eslint-disable-next-line no-unused-vars -- core rule false-positives on function-type param names under the TS parser
type Accessors<T> = Record<string, (row: T) => unknown>;

export function useSort<T>(rows: T[], accessors: Accessors<T>, initial?: SortState) {
  const [sort, setSort] = useState<SortState | null>(initial ?? null);

  const toggle = (key: string) =>
    setSort((s) =>
      s && s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }
    );

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const acc = accessors[sort.key];
    if (!acc) return rows;
    const mul = sort.dir === "asc" ? 1 : -1;
    return rows
      .slice()
      .sort((a, b) => {
        const va = acc(a);
        const vb = acc(b);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (typeof va === "number" && typeof vb === "number") return (va - vb) * mul;
        return String(va).localeCompare(String(vb), undefined, { numeric: true }) * mul;
      });
    // deps: accessors are inline literals at call sites and intentionally excluded
  }, [rows, sort]);

  return { sorted, sort, toggle };
}

export function SortHeader({
  label,
  k,
  sort,
  onToggle,
  right = false,
}: {
  label: string;
  k: string;
  sort: SortState | null;
  // eslint-disable-next-line no-unused-vars -- core rule false-positives on function-type param names under the TS parser
  onToggle: (key: string) => void;
  right?: boolean;
}) {
  const active = sort?.key === k;
  return (
    <th
      className={cn(
        "px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500",
        right ? "text-right" : "text-left"
      )}
    >
      <button
        onClick={() => onToggle(k)}
        className={cn(
          "inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-800",
          active && "text-gray-800",
          right && "flex-row-reverse"
        )}
        title="Sort by this column"
      >
        {label}
        {active ? (
          sort!.dir === "desc" ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronUp className="h-3 w-3" />
          )
        ) : (
          <ChevronsUpDown className="h-3 w-3 opacity-40" />
        )}
      </button>
    </th>
  );
}

/**
 * Collapsible card header for long lists — the same pattern used on the
 * Compliance page, packaged so every page can offer it.
 */
export function CollapsibleCardHeader({
  title,
  count,
  open,
  onToggle,
  collapseLabel = "Collapse",
  expandLabel = "Expand",
  subtitle,
}: {
  title: string;
  count?: number;
  open: boolean;
  onToggle: () => void;
  collapseLabel?: string;
  expandLabel?: string;
  subtitle?: string;
}) {
  return (
    <button
      onClick={onToggle}
      className="flex w-full items-center justify-between border-b border-gray-200 px-6 py-4 text-left hover:bg-gray-50"
    >
      <span>
        <span className="font-semibold text-gray-800">
          {title}
          {count != null && (
            <span className="ml-2 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {count}
            </span>
          )}
        </span>
        {subtitle && <span className="mt-0.5 block text-xs text-gray-500">{subtitle}</span>}
      </span>
      <span className="text-xs font-medium text-gray-500">{open ? collapseLabel : expandLabel}</span>
    </button>
  );
}
